/**
 * JumuahPrep — Friday checklist with progress dots, only renders on Friday.
 *
 * 6-step checklist (sahih sources cited inline):
 *   1. Ghusl (full ritual bath) — Bukhari 877
 *   2. Wear best/clean clothes — Bukhari 879
 *   3. Apply perfume / siwak — Bukhari 880
 *   4. Read Surah Al-Kahf (1-10 or all) — Mustadrak al-Hakim 6169 (sahih)
 *   5. Make abundant du'a (especially the last hour) — Bukhari 935
 *   6. Send blessings on the Prophet ﷺ — Abu Dawud 1047
 *
 * State persists per Friday in localStorage so the user gets a clean
 * checklist next week. Each tap = persist + rerender (zero typing).
 *
 * On Jumu'ah day, AnticipationEngine bumps this widget to the very top
 * (+50 score) — mirrors how Apple's Watch surfaces "Workout time" only
 * when relevant.
 */

import type { Widget, WidgetContext } from '../types';
import { gregorianToHijri } from '../../../services/HijriDate';

interface ChecklistItem {
  id: string;
  labelKey: string;
  source: string;
  emoji: string;
}

const CHECKLIST: ChecklistItem[] = [
  { id: 'ghusl', labelKey: 'jumuah_step_ghusl', source: 'Bukhari 877', emoji: '🚿' },
  { id: 'clothes', labelKey: 'jumuah_step_clothes', source: 'Bukhari 879', emoji: '👔' },
  { id: 'siwak', labelKey: 'jumuah_step_siwak', source: 'Bukhari 880', emoji: '🌿' },
  { id: 'kahf', labelKey: 'jumuah_step_kahf', source: 'al-Hakim 6169', emoji: '📖' },
  { id: 'duas', labelKey: 'jumuah_step_duas', source: 'Bukhari 935', emoji: '🤲' },
  { id: 'salawat', labelKey: 'jumuah_step_salawat', source: 'Abu Dawud 1047', emoji: '🌹' },
];

function getFridayKey(d: Date): string {
  // Use Hijri date so the key rolls over consistently with Islamic calendar.
  try {
    const h = gregorianToHijri(d);
    return `aweni_jumuah_${h.year}_${h.month}_${h.day}`;
  } catch {
    const iso = d.toISOString().slice(0, 10);
    return `aweni_jumuah_${iso}`;
  }
}

function loadProgress(key: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}
function saveProgress(key: string, p: Record<string, boolean>): void {
  try { localStorage.setItem(key, JSON.stringify(p)); } catch { /* */ }
}

export const JumuahPrep: Widget = {
  id: 'jumuah-prep',
  label: {
    en: "Jumu'ah prep",
    fr: "Préparation Jumu'ah",
    ar: 'استعداد الجمعة',
    es: "Preparación Yumu'ah",
    de: "Jumu'ah-Vorbereitung",
  },
  icon: '🕌',
  defaultVisible: true,
  defaultOrder: 24,
  defaultSize: 'compact',
  visibilityRule: () => new Date().getDay() === 5, // Friday only

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const key = getFridayKey(new Date());
    let progress = loadProgress(key);

    const renderShell = (): void => {
      const done = CHECKLIST.filter((c) => progress[c.id]).length;
      const total = CHECKLIST.length;
      const pct = (done / total) * 100;

      container.innerHTML = `
        <div style="background:linear-gradient(135deg, var(--ln-bg-card,#fff), var(--ln-bg-card,#fff));border:1px solid var(--ln-accent-ceremonial,#9C8348);border-radius:16px;padding:14px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <span style="font-size:18px;">🕌</span>
            <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_jumuah_title') || "Jumu'ah preparation")}</span>
            <span style="margin-inline-start:auto;font-size:11px;font-weight:700;color:var(--ln-accent-ceremonial,#9C8348);font-variant-numeric:tabular-nums;">${done}/${total}</span>
          </div>

          <div style="height:6px;background:var(--ln-bg-secondary,rgba(0,0,0,0.06));border-radius:999px;overflow:hidden;margin-bottom:10px;">
            <div style="height:100%;width:${pct}%;background:var(--ln-accent-ceremonial,#9C8348);transition:width 250ms ease;"></div>
          </div>

          <div style="display:flex;flex-direction:column;gap:6px;">
            ${CHECKLIST.map((c) => {
              const done = !!progress[c.id];
              return `
                <button type="button" data-step="${c.id}"
                  style="display:flex;align-items:center;gap:10px;width:100%;padding:8px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:${done ? 'var(--ln-accent-ceremonial,#9C8348)15' : 'transparent'};cursor:pointer;color:inherit;font:inherit;text-align:start;min-height:44px;">
                  <span style="display:inline-flex;width:22px;height:22px;border-radius:6px;align-items:center;justify-content:center;background:${done ? 'var(--ln-accent-ceremonial,#9C8348)' : 'transparent'};border:2px solid var(--ln-accent-ceremonial,#9C8348);color:#fff;flex-shrink:0;font-weight:700;font-size:13px;">${done ? '✓' : ''}</span>
                  <span style="font-size:18px;flex-shrink:0;">${c.emoji}</span>
                  <span style="flex:1;min-width:0;">
                    <span style="display:block;font-size:13px;font-weight:600;color:var(--ln-text-primary,#1E293B);">${escapeHtml(t(c.labelKey) || c.id)}</span>
                    <span style="display:block;font-size:10px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(c.source)}</span>
                  </span>
                </button>
              `;
            }).join('')}
          </div>

          <a href="/memoriz/#/surah/18" data-aweni-external="1" style="display:block;margin-top:10px;text-align:center;padding:10px;border-radius:10px;background:var(--ln-accent,#0A0A0A);color:var(--ln-accent-text,#fff);font-size:12px;font-weight:700;text-decoration:none;">📖 ${escapeHtml(t('widget_jumuah_open_kahf') || 'Open Surah Al-Kahf')}</a>
        </div>
      `;

      container.querySelectorAll<HTMLButtonElement>('[data-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.step || '';
          progress = { ...progress, [id]: !progress[id] };
          saveProgress(key, progress);
          renderShell();
        });
      });
    };

    renderShell();
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
